import { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';

// Custom hook for shared fire list logic
export const useFireList = (fires, additionalFilter = null) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isNewestFirst, setIsNewestFirst] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortFilter, setSortFilter] = useState('acres-burned');
  const [refreshing, setRefreshing] = useState(false);

  const debouncedSetter = useMemo(
    () => debounce((val) => setDebouncedSearch(val), 300),
    []
  );

  useEffect(() => {
    debouncedSetter(search);
    return () => debouncedSetter.cancel();
  }, [search, debouncedSetter]);

  const enhancedFires = useMemo(
    () =>
      fires.map((f) => ({
        ...f,
        nameLower: f.name.toLowerCase(),
        _startMs: new Date(f.startTimestamp).getTime(),
        _updateMs: new Date(f.updatedTimestamp || f.startTimestamp).getTime(),
      })),
    [fires]
  );

  const filteredFires = useMemo(() => {
    const ds = debouncedSearch.toLowerCase();

    let base = enhancedFires.filter((f) => f.nameLower.includes(ds));
    
    // Apply additional filter if provided (e.g., geom_check for Perimeters)
    if (additionalFilter) {
      base = base.filter(additionalFilter);
    }
    
    base = base.filter((f) => {
      if (statusFilter === 'all') return true;
      if (statusFilter === 'active') return f.active;
      if (statusFilter === 'contained') return !f.active;
      return true;
    });

    // Sort fires
    return base.sort((a, b) => {
      switch (sortFilter) {
        case 'acres-burned': {
          const aHas = a.areaAC != null;
          const bHas = b.areaAC != null;
          if (aHas && bHas) {
            return isNewestFirst
              ? b.areaAC - a.areaAC
              : a.areaAC - b.areaAC;
          }
          if (aHas) return -1;
          if (bHas) return 1;
          return 0;
        }

        case 'containment': {
          const aHas = a.perContained != null;
          const bHas = b.perContained != null;
          if (aHas && bHas) {
            return isNewestFirst
              ? b.perContained - a.perContained
              : a.perContained - b.perContained;
          }
          if (aHas) return -1;
          if (bHas) return 1;
          return 0;
        }
        
        case 'discovery-time':
          return isNewestFirst
            ? b._startMs - a._startMs
            : a._startMs - b._startMs;
            
        case 'last-updated':
        default:
          return isNewestFirst
            ? b._updateMs - a._updateMs
            : a._updateMs - b._updateMs;
      }
    });
  }, [enhancedFires, debouncedSearch, statusFilter, sortFilter, isNewestFirst, additionalFilter]);

  const handleRefresh = useCallback((getFires, map) => {
    setRefreshing(true);
    getFires(map, () => setRefreshing(false));
  }, []);

  return {
    search,
    setSearch,
    isNewestFirst,
    setIsNewestFirst,
    statusFilter,
    setStatusFilter,
    sortFilter,
    setSortFilter,
    refreshing,
    filteredFires,
    handleRefresh
  };
};
